<script setup lang="ts">
  import { ref, nextTick } from "vue";
  import type { Editor } from "@tiptap/vue-3";
  import { Bold, Italic, Link2, Unlink } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

  const props = defineProps<{
    editor: Editor;
  }>();

  const linkPopoverOpen = ref(false);
  const urlValue = ref("");
  const urlInput = ref<HTMLInputElement | null>(null);

  const openLinkPopover = () => {
    const existingHref = props.editor.getAttributes("link").href as string | undefined;
    urlValue.value = existingHref ?? "";
    linkPopoverOpen.value = true;
    nextTick(() => urlInput.value?.focus());
  };

  const applyLink = () => {
    const url = urlValue.value.trim();
    if (url) {
      props.editor.chain().focus().setLink({ href: url }).run();
    }
    linkPopoverOpen.value = false;
    urlValue.value = "";
  };
</script>

<template>
  <div class="flex items-center gap-1 bg-popover border rounded-md shadow-md p-1">
    <Button
      variant="ghost"
      size="sm"
      :class="{ 'bg-accent': editor.isActive('bold') }"
      @click="editor.chain().focus().toggleBold().run()">
      <Bold class="w-4 h-4" />
    </Button>
    <Button
      variant="ghost"
      size="sm"
      :class="{ 'bg-accent': editor.isActive('italic') }"
      @click="editor.chain().focus().toggleItalic().run()">
      <Italic class="w-4 h-4" />
    </Button>
    <template v-if="editor.isActive('link')">
      <Button
        variant="ghost"
        size="sm"
        class="bg-accent"
        @click="editor.chain().focus().unsetLink().run()">
        <Unlink class="w-4 h-4" />
      </Button>
    </template>
    <template v-else>
      <Popover v-model:open="linkPopoverOpen">
        <PopoverTrigger as-child>
          <Button
            variant="ghost"
            size="sm"
            @click="openLinkPopover">
            <Link2 class="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent class="w-64 p-2" @keydown.stop>
          <form class="flex gap-1" @submit.prevent="applyLink">
            <input
              ref="urlInput"
              v-model="urlValue"
              type="url"
              placeholder="https://..."
              class="flex-1 h-8 rounded-md border border-input bg-background px-2 text-13 outline-none focus:ring-1 focus:ring-ring" />
            <Button type="submit" size="sm">OK</Button>
          </form>
        </PopoverContent>
      </Popover>
    </template>
  </div>
</template>
